import {useCallback, useEffect, useMemo, useState} from 'react';

import {useField} from 'formik';
import {useTranslation} from 'react-i18next';

import {Autocomplete, Box, MenuItem, Stack, TextField, Typography, useTheme} from '@mui/material';

import {EmpionButton} from '../EmpionButton/EmpionButton';
import {EmpionLabeledValueList} from '../EmpionLabeledValueList/EmpionLabeledValueList';

import {LanguageCode, LanguageProficiency} from '../../domain/languages';
import {getFieldStyle} from '../../layouts/theme/FieldStyles';

interface Props {
  name: string;
}

type LanguageItem = {
  label: string;
  id: string;
  proficiency: LanguageProficiency;
};

export const LanguageInput = ({name}: Props) => {
  const {t} = useTranslation();
  const {t: td} = useTranslation('domain');
  const theme = useTheme();
  const [field, , helpers] = useField(name);
  const [languages, setLanguages] = useState<LanguageItem[]>([]);
  const [editOption, setEditOption] = useState<{label: string; id: string} | null>(null);
  const [editLevel, setEditLevel] = useState<LanguageProficiency>(LanguageProficiency.C2);

  useEffect(() => {
    const value = field.value as LanguageItem[];
    setLanguages(value.map((v) => ({label: td(`languageEnum.${v.id}`), id: v.id, proficiency: v.proficiency})));
    // NOTE: This should only run once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveLanguage = useCallback(
    (language: string) => {
      setLanguages(languages.filter((s) => s.id !== language));
    },
    [languages]
  );

  const remainingOptions = useMemo(() => {
    return Object.values(LanguageCode)
      .map((code) => ({label: td(`languageEnum.${code}`), id: code}))
      .filter((option) => !languages.find((l) => l.id === option.id))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [languages, td]);
  const themedTextField = getFieldStyle(theme);

  useEffect(() => {
    helpers.setValue(languages);
  }, [languages, helpers]);

  return (
    <>
      <Typography variant='h2' gutterBottom>
        {t('shared.formMaker.languages.title')}
      </Typography>
      <Typography variant='body1' gutterBottom>
        {t('shared.formMaker.languages.description')}
      </Typography>

      <Autocomplete
        value={editOption}
        onChange={(event, newValue) => {
          setEditOption(newValue);
        }}
        id='combo-box-demo'
        options={remainingOptions}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            size='small'
            fullWidth
            variant='outlined'
            sx={themedTextField}
            data-testid='autocomplete-language-input'
          />
        )}
      />
      <Stack direction='row' spacing={1} sx={{marginTop: 1}}>
        <TextField
          select
          sx={themedTextField}
          fullWidth
          size='small'
          value={editLevel}
          onChange={(e) => setEditLevel(e.target.value as LanguageProficiency)}
          variant='outlined'
          data-testid='language-level-input'
        >
          {Object.keys(LanguageProficiency)
            .map((level) => ({
              id: level,
              label: level,
            }))
            .map((option) => (
              <MenuItem key={option.id} value={option.id} data-testid={`language-level-${option.id}`}>
                {option.label}
              </MenuItem>
            ))}
        </TextField>
      </Stack>
      <EmpionButton
        sx={{
          mt: 1,
        }}
        fullWidth
        onClick={() => {
          if (editOption && !languages.find((l) => l.id === editOption.id)) {
            setLanguages([
              ...languages,
              {
                label: editOption.label,
                id: editOption.id,
                proficiency: editLevel,
              },
            ]);
            setEditLevel(LanguageProficiency.C2);
          }
        }}
        data-testid='language-add-button'
      >
        {t('shared.formButtons.add')}
      </EmpionButton>
      <Box>
        <EmpionLabeledValueList
          light
          title={''}
          onDelete={(language) => handleRemoveLanguage(language)}
          groups={[
            {
              color: 'secondary',
              items: languages.map((language) => ({
                id: language.id,
                label: language.label,
                value: language.proficiency,
              })),
            },
          ]}
        />
      </Box>
    </>
  );
};
